<template>
  <div
    id="container"
    class="overflow-x-auto flex scrollbar-none space-x-1 -ml-4 sm:-ml-6 lg:ml-0 -mr-4 sm:-mr-6 lg:mr-0 pr-4 sm:pr-6 lg:pr-0"
  >
    <button
      v-for="(timeslot, index) in timeslots"
      :key="index"
      :aria-label="$t('quote.select-timeslot', {
        timeslot: getAppointmentTime(timeslot.start_at),
      })"
      type="button"
      :class="{
        border: isTimeslotSelected(timeslot),
        'border-2': !isTimeslotSelected(timeslot),
      }"
      class="flex shrink-0 w-40 h-20 rounded-lg border-transparent bg-white first:ml-4 sm:first:ml-6 lg:first:ml-0"
      @click=" $store.dispatch(
        'quote/updateTimeslotAndFetchQuote',
        timeslot,
      )"
    >
      <span
        :class="{
          'border-2 border-bubblegum-500': isTimeslotSelected(timeslot),
          'border-neutral-900 border': !isTimeslotSelected(timeslot),
        }"
        class="size-full shrink-0 rounded-lg flex flex-col justify-center items-center"
      >
        <span v-show="isTimeslotSelected(timeslot) && isQuoteLoading">
          <fa
            :icon="['fas', 'circle-notch']"
            class="text-base text-bubblegum-700"
            spin
          />
        </span>
        <span
          v-show="!isQuoteLoading || !isTimeslotSelected(timeslot)"
          class="flex flex-col justify-center gap-1 items-center"
        >
          <span
            class="text-xs text-neutral-500"
            :class="{ 'mt-0.5': !hidePricing, '-mt-0.5': hidePricing }"
          >
            {{ buttonDate(timeslot.start_at, getInputs.timezone) }}
          </span>
          <span
            :class="{ 'text-bubblegum-700': isTimeslotSelected(timeslot) }"
            class="block font-semibold text-base/3"
          >
            {{ getTime(timeslot.start_at) }}
          </span>
          <vetster-discount
            v-if="!hidePricing"
            class="text-sm text-neutral-500"
          >
            {{ getAmount(timeslot) }}
          </vetster-discount>
        </span>
      </span>
    </button>
  </div>
</template>

<script>
import find from 'lodash/find';
import { DateTime } from 'luxon';
import { getCurrentInstance } from 'vue';
import { mapGetters } from 'vuex';

import VetsterDiscount from '@/components/VetsterDiscount';
import useAppointmentDatetimeFormat from '@/composables/use-appointment-datetime-format';
import useCheckoutHelpers from '@/composables/use-checkout-helpers';
import useTimeslotHelpers from '@/composables/use-timeslot-helpers';
import { formatMoneyRounded } from '@/helpers/format';
import HasConversionRate from '@/mixins/HasConversionRate';
import SelectsTimeslot from '@/mixins/SelectsTimeslot';

export default {
  name: 'VetsterPartnerTimeslots',

  components: { VetsterDiscount },

  mixins: [
    HasConversionRate,
    SelectsTimeslot,
  ],

  props: {
    partnerTimeslots: {
      type: Array,
      default: () => [],
    },
    previousTimeslot: {
      type: [String, Object],
      default: undefined,
    },
    timezone: {
      type: String,
      default: undefined,
    },
  },

  setup() {
    const root = getCurrentInstance().proxy.$root;
    const { getAppointmentTime } = useAppointmentDatetimeFormat();
    const { isSubscription } = useCheckoutHelpers(root);
    const { buttonDate } = useTimeslotHelpers();

    return {
      buttonDate,
      getAppointmentTime,
      isSubscription,
    };
  },

  data() {
    return {
      selectedDate: null,
      timeslots: null,
      timeslotsInitialSize: null,
    };
  },

  computed: {
    ...mapGetters('quote', [
      'hasAvailability',
      'isQuoteLoading',
      'isStandardPricing',
    ]),
    ...mapGetters('search', ['hidePriceForSearchPet']),
    hidePricing() {
      return this.hidePriceForSearchPet || this.isSubscription || this.isStandardPricing;
    },
  },

  watch: {
    previousTimeslot: {
      async handler(timeslot, oldTimeslot) {
        // Only remove a timeslot if we have modified the original array
        if (this.timeslotsInitialSize < this.timeslots.length) {
          this.timeslots.pop();
        }

        if (timeslot && timeslot !== oldTimeslot && !find(this.timeslots, timeslot)) {
          this.timeslots.push(timeslot);
          this.$nextTick(() => {
            document.getElementById('container').scrollLeft = document.getElementById('container').scrollWidth;
          });
        }

        if (!timeslot) {
          this.$store.dispatch('quote/updateTimeslotAndFetchQuote', this.timeslots[0]);
          this.$nextTick(() => {
            document.getElementById('container').scrollLeft = 0;
          });
        }
      },
      deep: true,
    },
  },

  mounted() {
    this.timeslots = this.partnerTimeslots;
    this.timeslotsInitialSize = this.timeslots.length;
  },

  methods: {
    getAmount(timeslot) {
      const rate = this.convertCurrency(timeslot.rate.amount);

      return this.$n(this.formatMoneyRounded(rate), {
        style: 'currency',
        currency: this.getConversionCurrency || timeslot.rate.currency,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });
    },
    formatMoneyRounded,
    getTime(timeslot) {
      const formatedDay = DateTime.fromISO(timeslot, {
        zone: this.timezone,
      });

      return formatedDay.toFormat('t');
    },
  },
};
</script>
